import React, { useEffect, useMemo, useRef, useState } from "react";
import Sidebar from "../../components/Sidebar/sidebar";
import Header from "../../components/Header";
import api from "../../utils/api";
import {
  FaCompressArrowsAlt,
  FaEdit,
  FaEye,
  FaInfoCircle,
  FaKey,
  FaMinus,
  FaPlus,
  FaProjectDiagram,
  FaSearch,
  FaSitemap,
  FaTimes,
  FaTrash,
  FaUserFriends
} from "react-icons/fa";
import { getUserPrivileges, getUserRole } from "../../utils/auth";
import { EMPLOYEE_MANAGEMENT_PRIVILEGES } from "../../utils/employeeManagementPrivileges";
import "./ManageUsers.css";
import { API_BASE_URL } from '../../utils/apiBase';

const titleCase = (value = "") =>
  value
    .replace(/([A-Z])/g, " $1")
    .replace(/[._-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "U";

const ManageUsers = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sortBy, setSortBy] = useState("employeeId");
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [editUser, setEditUser] = useState(null);
  const [editError, setEditError] = useState("");
  const [statusUser, setStatusUser] = useState(null);
  const [statusDate, setStatusDate] = useState("");
  const [statusError, setStatusError] = useState("");
  const [confirmInactiveUser, setConfirmInactiveUser] = useState(null);
  const [confirmError, setConfirmError] = useState("");
  const [viewUser, setViewUser] = useState(null);
  const [deleteUser, setDeleteUser] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const [resetUser, setResetUser] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetError, setResetError] = useState("");
  const [adminOptions, setAdminOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [managerFilter, setManagerFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [hierarchyOpen, setHierarchyOpen] = useState(false);
  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const [hierarchyError, setHierarchyError] = useState("");
  const [hierarchyRoots, setHierarchyRoots] = useState([]);
  const [hierarchyScope, setHierarchyScope] = useState("organization");
  const [collapsedNodes, setCollapsedNodes] = useState({});
  const [hierarchySearch, setHierarchySearch] = useState("");
  const [selectedHierarchyUser, setSelectedHierarchyUser] = useState(null);
  const [chartScale, setChartScale] = useState(1);
  const [chartOffset, setChartOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });

  const userRole = getUserRole();
  const userPrivileges = getUserPrivileges();
  const isAdmin = userRole === "admin";
  const userOperationPrivileges = EMPLOYEE_MANAGEMENT_PRIVILEGES.users;
  const hasUserPrivilege = (key) => isAdmin || userPrivileges.includes(key);
  const canExportUsers = hasUserPrivilege(userOperationPrivileges.export);
  const canViewUser = hasUserPrivilege(userOperationPrivileges.view);
  const canEditUser = hasUserPrivilege(userOperationPrivileges.edit);
  const canResetPassword = hasUserPrivilege(userOperationPrivileges.resetPassword);
  const canViewHierarchy = hasUserPrivilege(userOperationPrivileges.viewHierarchy);
  const canDeleteUser = hasUserPrivilege(userOperationPrivileges.delete);
  const canListUsers = userOperationPrivileges.access.some(hasUserPrivilege);
  const canShowUserActions = canViewUser || canEditUser || canResetPassword || canViewHierarchy || canDeleteUser;
  const canEditStatus = canEditUser;

  const params = useMemo(() => {
    const trimmed = search.trim();
    return trimmed ? { q: trimmed } : {};
  }, [search]);

  useEffect(() => {
    let alive = true;

    const fetchUsers = async () => {
      if (!canListUsers) {
        setUsers([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const res = await api.get(`${API_BASE_URL}/api/auth/users`, {
          params
        });
        if (alive) {
          setUsers(res.data || []);
        }
      } catch (err) {
        if (alive) {
          setError("Failed to load users.");
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    };

    fetchUsers();

    return () => {
      alive = false;
    };
  }, [params, canListUsers]);

  useEffect(() => {
    setPage(1);
  }, [departmentFilter, managerFilter, roleFilter, search, statusFilter, pageSize]);

  useEffect(() => {
    let alive = true;

    const fetchAdmins = async () => {
      if (!canEditUser && !canViewHierarchy) return;

      try {
        const res = await api.get(`${API_BASE_URL}/api/auth/employee-options`);
        if (alive) {
          setAdminOptions(res.data || []);
        }
      } catch (err) {
        if (alive) {
          setError("Failed to load reporting managers.");
        }
      }
    };

    fetchAdmins();

    return () => {
      alive = false;
    };
  }, [canEditUser, canViewHierarchy]);

  useEffect(() => {
    let alive = true;

    const fetchRoles = async () => {
      if (!canEditUser) return;

      try {
        const res = await api.get(`${API_BASE_URL}/api/roles/options`);
        if (alive) {
          setRoleOptions(res.data || []);
        }
      } catch (err) {
        if (alive) {
          setError("Failed to load roles.");
        }
      }
    };

    fetchRoles();

    return () => {
      alive = false;
    };
  }, [canEditUser]);

  const roleFilterOptions = useMemo(() => {
    const roleNames = users.map((user) => user.roleName || titleCase(user.role || "User"));
    return [...new Set(roleNames)].sort((a, b) => a.localeCompare(b));
  }, [users]);

  const managerFilterOptions = useMemo(() => {
    const managers = users
      .map((user) => user.reportingManager?.managerName)
      .filter(Boolean);
    return [...new Set(managers)].sort((a, b) => a.localeCompare(b));
  }, [users]);

  const departmentFilterOptions = useMemo(() => {
    const departments = users.map((user) => user.department).filter(Boolean);
    return [...new Set(departments)].sort((a, b) => a.localeCompare(b));
  }, [users]);

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const displayRole = user.roleName || titleCase(user.role || "User");
      const status = user.status || "active";
      const managerName = user.reportingManager?.managerName || "";
      const department = user.department || "";

      const matchesRole = roleFilter === "all" || displayRole === roleFilter;
      const matchesStatus = statusFilter === "all" || status === statusFilter;
      const matchesManager = managerFilter === "all" || managerName === managerFilter;
      const matchesDepartment = departmentFilter === "all" || department === departmentFilter;

      return matchesRole && matchesStatus && matchesManager && matchesDepartment;
    });
  }, [departmentFilter, managerFilter, roleFilter, statusFilter, users]);

  const sortedUsers = useMemo(() => {
    const list = [...filteredUsers];
    const direction = sortDir === "asc" ? 1 : -1;

    list.sort((a, b) => {
      const aVal = (a?.[sortBy] ?? "").toString().toLowerCase();
      const bVal = (b?.[sortBy] ?? "").toString().toLowerCase();
      if (aVal < bVal) return -1 * direction;
      if (aVal > bVal) return 1 * direction;
      return 0;
    });

    return list;
  }, [filteredUsers, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedUsers = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sortedUsers.slice(start, start + pageSize);
  }, [sortedUsers, safePage, pageSize]);

  const hierarchyMatches = useMemo(() => {
    const search = hierarchySearch.trim().toLowerCase();
    const matches = new Set();
    const path = new Set();

    const visit = (node, ancestors = []) => {
      const searchable = [
        node.name,
        node.employeeId,
        node.email,
        node.roleName,
        node.role,
        node.reportingManager?.managerName
      ]
        .join(" ")
        .toLowerCase();

      if (search && searchable.includes(search)) {
        matches.add(String(node._id));
        ancestors.forEach((id) => path.add(String(id)));
      }

      (node.children || []).forEach((child) => visit(child, [...ancestors, node._id]));
    };

    hierarchyRoots.forEach((root) => visit(root));
    return { matches, path };
  }, [hierarchyRoots, hierarchySearch]);

  const openHierarchy = async () => {
    setHierarchyOpen(true);
    setHierarchyLoading(true);
    setHierarchyError("");
    setSelectedHierarchyUser(null);
    setChartScale(1);
    setChartOffset({ x: 0, y: 0 });

    try {
      const res = await api.get(`${API_BASE_URL}/api/auth/users/hierarchy`);
      setHierarchyRoots(res.data?.roots || []);
      setHierarchyScope(res.data?.scope || "organization");
      setCollapsedNodes({});
    } catch (err) {
      setHierarchyError(err.response?.data?.error || "Failed to load hierarchy.");
    } finally {
      setHierarchyLoading(false);
    }
  };

  const openHierarchyForUser = async (user) => {
    await openHierarchy();
    setHierarchySearch(user.employeeId || user.name || "");
  };

  const closeHierarchy = () => {
    setHierarchyOpen(false);
    setHierarchySearch("");
    setSelectedHierarchyUser(null);
    setIsPanning(false);
  };

  const toggleHierarchyNode = (nodeId) => {
    setCollapsedNodes((current) => ({
      ...current,
      [nodeId]: !current[nodeId]
    }));
  };

  const expandAllHierarchy = () => setCollapsedNodes({});

  const collapseAllHierarchy = () => {
    const next = {};
    const visit = (node) => {
      if ((node.children || []).length > 0) {
        next[node._id] = true;
        node.children.forEach(visit);
      }
    };
    hierarchyRoots.forEach(visit);
    setCollapsedNodes(next);
  };

  const resetChartView = () => {
    setChartScale(1);
    setChartOffset({ x: 0, y: 0 });
  };

  const startPan = (e) => {
    if (e.button !== 0) return;
    setIsPanning(true);
    panStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      offsetX: chartOffset.x,
      offsetY: chartOffset.y
    };
  };

  const movePan = (e) => {
    if (!isPanning) return;
    setChartOffset({
      x: panStartRef.current.offsetX + e.clientX - panStartRef.current.x,
      y: panStartRef.current.offsetY + e.clientY - panStartRef.current.y
    });
  };

  const stopPan = () => setIsPanning(false);

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(key);
    setSortDir("asc");
  };

  const updateUser = async (id, payload) => {
    const res = await api.put(`${API_BASE_URL}/api/auth/users/${id}`, payload);
    const updated = res.data;
    setUsers((prev) => prev.map((u) => (u._id === updated._id ? updated : u)));
  };

  const openEdit = (user) => {
    setEditError("");
    setEditUser({
      ...user,
      status: user.status || "active",
      roleId: user.roleId || "",
      reportingManagerId: user.reportingManager?.managerId || "",
      resignationDate: user.resignationDate
        ? new Date(user.resignationDate).toISOString().slice(0, 10)
        : ""
    });
  };

  const closeEdit = () => {
    setEditUser(null);
    setEditError("");
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editUser) return;

    if (editUser.status === "inactive" && !editUser.resignationDate) {
      setEditError("Resignation date is required for inactive users.");
      return;
    }

    try {
      await updateUser(editUser._id, {
        name: editUser.name,
        username: editUser.username,
        email: editUser.email,
        role: editUser.role,
        roleId: editUser.roleId || null,
        reportingManagerId: editUser.reportingManagerId || null,
        status: editUser.status,
        resignationDate: editUser.status === "inactive" ? editUser.resignationDate : null
      });
      closeEdit();
    } catch (err) {
      setEditError(err.response?.data?.error || "Failed to update user.");
    }
  };

  const handleStatusChange = (user, newStatus) => {
    if (newStatus === (user.status || "active")) return;

    if (newStatus === "inactive") {
      setConfirmError("");
      setConfirmInactiveUser(user);
      return;
    }

    updateUser(user._id, {
      name: user.name,
      username: user.username,
      email: user.email,
      role: user.role,
      status: "active",
      resignationDate: null
    }).catch(() => {
      setError("Failed to update status.");
    });
  };

  const confirmInactive = () => {
    if (!confirmInactiveUser) return;
    setStatusError("");
    setStatusDate("");
    setStatusUser(confirmInactiveUser);
    setConfirmInactiveUser(null);
  };

  const cancelConfirmInactive = () => {
    setConfirmInactiveUser(null);
    setConfirmError("");
  };

  const submitResignation = async (e) => {
    e.preventDefault();
    if (!statusUser) return;

    if (!statusDate) {
      setStatusError("Resignation date is required.");
      return;
    }

    try {
      await updateUser(statusUser._id, {
        name: statusUser.name,
        username: statusUser.username,
        email: statusUser.email,
        role: statusUser.role,
        status: "inactive",
        resignationDate: statusDate
      });
      setStatusUser(null);
      setStatusDate("");
      setStatusError("");
    } catch (err) {
      setStatusError(err.response?.data?.error || "Failed to update status.");
    }
  };

  const closeStatusModal = () => {
    setStatusUser(null);
    setStatusDate("");
    setStatusError("");
  };

  const confirmDeleteUser = async () => {
    if (!deleteUser) return;
    setDeleteError("");

    try {
      await api.delete(`${API_BASE_URL}/api/auth/users/${deleteUser._id}`);
      setUsers((current) => current.filter((user) => user._id !== deleteUser._id));
      setDeleteUser(null);
    } catch (err) {
      setDeleteError(err.response?.data?.error || "Failed to delete user.");
    }
  };

  const submitResetPassword = async (e) => {
    e.preventDefault();
    if (!resetUser) return;

    if (!resetPassword || resetPassword.length < 6) {
      setResetError("Password must be at least 6 characters.");
      return;
    }

    try {
      await api.put(`${API_BASE_URL}/api/auth/users/${resetUser._id}/reset-password`, {
        newPassword: resetPassword
      });
      setResetUser(null);
      setResetPassword("");
      setResetError("");
    } catch (err) {
      setResetError(err.response?.data?.error || "Failed to reset password.");
    }
  };

  const exportCsv = () => {
    const headers = ["Employee ID", "Name", "Username", "Email", "Access Level", "Role", "Reporting Manager", "Status", "Resignation Date"];
    const rows = sortedUsers.map((u) => [
      u.employeeId || "",
      u.name || "",
      u.username || "",
      u.email || "",
      u.role || "",
      u.roleName || "",
      u.reportingManager?.managerName || "",
      u.status || "active",
      u.resignationDate ? new Date(u.resignationDate).toISOString().slice(0, 10) : ""
    ]);

    const escapeCell = (value) => {
      const cell = String(value ?? "");
      if (/["\n,]/.test(cell)) {
        return `"${cell.replace(/"/g, '""')}"`;
      }
      return cell;
    };

    const csv = [
      headers.map(escapeCell).join(","),
      ...rows.map((row) => row.map(escapeCell).join(","))
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "users.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const renderHierarchyNode = (node) => {
    const nodeId = String(node._id);
    const children = node.children || [];
    const isCollapsed = collapsedNodes[nodeId];
    const isMatch = hierarchyMatches.matches.has(nodeId);
    const isInPath = hierarchyMatches.path.has(nodeId);
    const displayRole = node.roleName || titleCase(node.role || "User");

    return (
      <li key={nodeId} className={`org-node-item ${children.length ? "has-children" : ""}`}>
        <div className={`org-node-card ${isMatch ? "match" : ""} ${isInPath ? "path" : ""}`}>
          <div className="org-avatar">{getInitials(node.name)}</div>
          <div className="org-node-info">
            <strong>{node.name}</strong>
            <span>{node.employeeId || "No Employee ID"}</span>
            <small>{displayRole}</small>
          </div>
          <div className="org-node-actions">
            <button
              type="button"
              title="View employee info"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedHierarchyUser(node);
              }}
            >
              <FaInfoCircle />
            </button>
            {children.length > 0 && (
              <button
                type="button"
                title={isCollapsed ? "Expand team" : "Collapse team"}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleHierarchyNode(nodeId);
                }}
              >
                {isCollapsed ? <FaPlus /> : <FaMinus />}
              </button>
            )}
          </div>
          {children.length > 0 && (
            <div className="org-team-count">{children.length}</div>
          )}
        </div>

        {children.length > 0 && !isCollapsed && (
          <ul className="org-children">
            {children.map((child) => renderHierarchyNode(child))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <div className="manage-users-container">
      <div className="sidebar">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>
      <div className={`manage-users-content ${collapsed ? "collapsed" : ""}`}>
        <div className="sticky-header">
          <Header />
        </div>
        <div className="manage-users-scroll-area">
          <div className="manage-users-heading">
            <div>
              <span><FaUserFriends /> Employee Directory</span>
              <h2>Manage Users</h2>
              <p>Search, filter, update access, and inspect the reporting hierarchy.</p>
            </div>
            <div className="manage-users-summary">
              <strong>{filteredUsers.length}</strong>
              <span>shown of {users.length}</span>
            </div>
          </div>

          <div className="manage-users-toolbar">
            <label className="manage-users-search">
              <FaSearch />
              <input
                type="text"
                placeholder="Search by name, employee ID, username, or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            {canViewHierarchy && (
              <button
                type="button"
                className="manage-users-export"
                onClick={openHierarchy}
              >
                <FaSitemap /> View Hierarchy
              </button>
            )}
            {canExportUsers && (
              <button
                type="button"
                className="manage-users-export"
                onClick={exportCsv}
                disabled={sortedUsers.length === 0}
              >
                Export CSV
              </button>
            )}
          </div>

          <div className="manage-users-filters">
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="all">All Roles</option>
              {roleFilterOptions.map((roleName) => (
                <option key={roleName} value={roleName}>{roleName}</option>
              ))}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select value={managerFilter} onChange={(e) => setManagerFilter(e.target.value)}>
              <option value="all">All Reporting Managers</option>
              {managerFilterOptions.map((managerName) => (
                <option key={managerName} value={managerName}>{managerName}</option>
              ))}
            </select>
            <select value={departmentFilter} onChange={(e) => setDepartmentFilter(e.target.value)}>
              <option value="all">All Departments</option>
              {departmentFilterOptions.map((department) => (
                <option key={department} value={department}>{department}</option>
              ))}
            </select>
          </div>

          {error && <p className="error-text">{error}</p>}

          <div className="manage-users-table-wrap">
            {loading ? (
              <div className="manage-users-skeleton">
                {Array.from({ length: 7 }).map((_, index) => (
                  <span key={index} />
                ))}
              </div>
            ) : (
              <table className="manage-users-table">
                <thead>
                  <tr>
                    <th onClick={() => toggleSort("employeeId")}>
                      Employee ID
                      {sortBy === "employeeId" && (
                        <span className="sort-indicator">
                          {sortDir === "asc" ? "ASC" : "DESC"}
                        </span>
                      )}
                    </th>
                    <th onClick={() => toggleSort("name")}>
                      Name
                      {sortBy === "name" && (
                        <span className="sort-indicator">
                          {sortDir === "asc" ? "ASC" : "DESC"}
                        </span>
                      )}
                    </th>
                    <th onClick={() => toggleSort("username")}>
                      Username
                      {sortBy === "username" && (
                        <span className="sort-indicator">
                          {sortDir === "asc" ? "ASC" : "DESC"}
                        </span>
                      )}
                    </th>
                    <th onClick={() => toggleSort("email")}>
                      Email
                      {sortBy === "email" && (
                        <span className="sort-indicator">
                          {sortDir === "asc" ? "ASC" : "DESC"}
                        </span>
                      )}
                    </th>
                    <th onClick={() => toggleSort("role")}>
                      Access Level
                      {sortBy === "role" && (
                        <span className="sort-indicator">
                          {sortDir === "asc" ? "ASC" : "DESC"}
                        </span>
                      )}
                    </th>
                    <th onClick={() => toggleSort("roleName")}>
                      Role
                      {sortBy === "roleName" && (
                        <span className="sort-indicator">
                          {sortDir === "asc" ? "ASC" : "DESC"}
                        </span>
                      )}
                    </th>
                    <th>Reporting Manager</th>
                    <th onClick={() => toggleSort("status")}>
                      Status
                      {sortBy === "status" && (
                        <span className="sort-indicator">
                          {sortDir === "asc" ? "ASC" : "DESC"}
                        </span>
                      )}
                    </th>
                    {canShowUserActions && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {pagedUsers.length === 0 ? (
                    <tr>
                      <td colSpan={canShowUserActions ? 9 : 8} className="manage-users-empty">
                        <FaUserFriends />
                        <strong>No users found</strong>
                        <span>Try changing your search or filters.</span>
                      </td>
                    </tr>
                  ) : (
                    pagedUsers.map((u) => (
                      <tr key={u._id}>
                        <td>{u.employeeId || "-"}</td>
                        <td>
                          <div className="user-identity">
                            <span className="user-avatar">{getInitials(u.name)}</span>
                            <div>
                              <strong>{u.name}</strong>
                              <small>{u.employeeId || "No Employee ID"}</small>
                            </div>
                          </div>
                        </td>
                        <td>{u.username}</td>
                        <td>{u.email}</td>
                        <td>
                          <span className={`access-badge ${u.role === "admin" ? "admin" : "user"}`}>
                            {titleCase(u.role || "User")}
                          </span>
                        </td>
                        <td>
                          <span className="role-badge">{u.roleName || "-"}</span>
                        </td>
                        <td>{u.reportingManager?.managerName || "-"}</td>
                        <td>
                          <div className="status-cell">
                            <span className={`status-badge ${u.status || "active"}`}>
                              {titleCase(u.status || "active")}
                            </span>
                            <select
                              className="status-select"
                              value={u.status || "active"}
                              onChange={(e) => handleStatusChange(u, e.target.value)}
                              disabled={!canEditStatus}
                              title="Change status"
                            >
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </div>
                        </td>
                        {canShowUserActions && <td className="action-cell">
                          <div className="action-buttons">
                            {canViewUser && (
                              <button type="button" className="manage-user-icon-button" onClick={() => setViewUser(u)} aria-label="View user" title="View">
                                <FaEye />
                              </button>
                            )}
                            {canEditUser && (
                              <button type="button" className="manage-user-icon-button" onClick={() => openEdit(u)} aria-label="Edit user" title="Edit">
                                <FaEdit />
                              </button>
                            )}
                            {canResetPassword && (
                              <button type="button" className="manage-user-icon-button" onClick={() => setResetUser(u)} aria-label="Reset password" title="Reset Password">
                                <FaKey />
                              </button>
                            )}
                            {canViewHierarchy && (
                              <button type="button" className="manage-user-icon-button" onClick={() => openHierarchyForUser(u)} aria-label="View hierarchy" title="View Hierarchy">
                                <FaSitemap />
                              </button>
                            )}
                            {canDeleteUser && (
                              <button type="button" className="manage-user-icon-button danger" onClick={() => setDeleteUser(u)} aria-label="Delete user" title="Delete">
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </td>}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>

          <div className="manage-users-pagination">
            <div className="pagination-info">
              Page {safePage} of {totalPages} - {sortedUsers.length} users
            </div>
            <div className="pagination-controls">
              <button
                type="button"
                onClick={() => setPage(1)}
                disabled={safePage === 1}
              >
                First
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
              >
                Prev
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
              >
                Next
              </button>
              <button
                type="button"
                onClick={() => setPage(totalPages)}
                disabled={safePage === totalPages}
              >
                Last
              </button>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {hierarchyOpen && (
        <div className="hierarchy-modal-overlay">
          <div className="hierarchy-modal">
            <div className="hierarchy-modal-header">
              <div>
                <span><FaProjectDiagram /> {hierarchyScope === "organization" ? "Organization" : "My Team"}</span>
                <h3>Organization Hierarchy</h3>
              </div>
              <button type="button" className="hierarchy-close" onClick={closeHierarchy}>
                <FaTimes />
              </button>
            </div>

            <div className="hierarchy-toolbar">
              <label className="hierarchy-search">
                <FaSearch />
                <input
                  value={hierarchySearch}
                  onChange={(e) => setHierarchySearch(e.target.value)}
                  placeholder="Search by name, employee ID, role, or manager"
                />
              </label>
              <div className="hierarchy-control-group">
                <button type="button" onClick={() => setChartScale((value) => Math.min(1.6, value + 0.1))}>
                  <FaPlus /> Zoom
                </button>
                <button type="button" onClick={() => setChartScale((value) => Math.max(0.55, value - 0.1))}>
                  <FaMinus /> Zoom
                </button>
                <button type="button" onClick={resetChartView}>
                  <FaCompressArrowsAlt /> Reset
                </button>
                <button type="button" onClick={expandAllHierarchy}>Expand All</button>
                <button type="button" onClick={collapseAllHierarchy}>Collapse All</button>
              </div>
            </div>

            <div className="hierarchy-body">
              {hierarchyLoading ? (
                <div className="hierarchy-state">Loading hierarchy...</div>
              ) : hierarchyError ? (
                <div className="hierarchy-state error">{hierarchyError}</div>
              ) : hierarchyRoots.length === 0 ? (
                <div className="hierarchy-state">No hierarchy data found.</div>
              ) : (
                <div
                  className={`org-chart-viewport ${isPanning ? "panning" : ""}`}
                  onMouseDown={startPan}
                  onMouseMove={movePan}
                  onMouseUp={stopPan}
                  onMouseLeave={stopPan}
                >
                  <div
                    className="org-chart-canvas"
                    style={{
                      transform: `translate(${chartOffset.x}px, ${chartOffset.y}px) scale(${chartScale})`
                    }}
                  >
                    <ul className="org-tree">
                      {hierarchyRoots.map((root) => renderHierarchyNode(root))}
                    </ul>
                  </div>
                </div>
              )}

              {selectedHierarchyUser && (
                <aside className="hierarchy-info-panel">
                  <button
                    type="button"
                    className="hierarchy-info-close"
                    onClick={() => setSelectedHierarchyUser(null)}
                  >
                    <FaTimes />
                  </button>
                  <div className="hierarchy-info-avatar">
                    {getInitials(selectedHierarchyUser.name)}
                  </div>
                  <h4>{selectedHierarchyUser.name}</h4>
                  <p>{selectedHierarchyUser.employeeId || "No Employee ID"}</p>
                  <dl>
                    <div>
                      <dt>Role</dt>
                      <dd>{selectedHierarchyUser.roleName || titleCase(selectedHierarchyUser.role || "User")}</dd>
                    </div>
                    <div>
                      <dt>Access Level</dt>
                      <dd>{titleCase(selectedHierarchyUser.role || "User")}</dd>
                    </div>
                    <div>
                      <dt>Reporting Manager</dt>
                      <dd>{selectedHierarchyUser.reportingManager?.managerName || "-"}</dd>
                    </div>
                    <div>
                      <dt>Email</dt>
                      <dd>{selectedHierarchyUser.email || "-"}</dd>
                    </div>
                    <div>
                      <dt>Phone</dt>
                      <dd>{selectedHierarchyUser.phoneNumber || "-"}</dd>
                    </div>
                    <div>
                      <dt>Team Size</dt>
                      <dd>{selectedHierarchyUser.children?.length || 0}</dd>
                    </div>
                  </dl>
                </aside>
              )}
            </div>
          </div>
        </div>
      )}

      {viewUser && (
        <div className="manage-users-modal-overlay">
          <div className="manage-users-modal user-profile-modal">
            <div className="modal-header profile-header">
              <span className="profile-avatar">{getInitials(viewUser.name)}</span>
              <div>
                <h3>{viewUser.name}</h3>
                <p>{viewUser.employeeId || "No Employee ID"}</p>
              </div>
            </div>
            <div className="profile-grid">
              <div><span>Username</span><strong>{viewUser.username || "-"}</strong></div>
              <div><span>Email</span><strong>{viewUser.email || "-"}</strong></div>
              <div><span>Access Level</span><strong>{titleCase(viewUser.role || "User")}</strong></div>
              <div><span>Role</span><strong>{viewUser.roleName || "-"}</strong></div>
              <div><span>Reporting Manager</span><strong>{viewUser.reportingManager?.managerName || "-"}</strong></div>
              <div><span>Status</span><strong>{titleCase(viewUser.status || "active")}</strong></div>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setViewUser(null)}>Close</button>
              <button type="button" className="primary" onClick={() => { openEdit(viewUser); setViewUser(null); }}>
                Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteUser && (
        <div className="manage-users-modal-overlay">
          <div className="manage-users-modal confirm-modal">
            <div className="modal-header">
              <h3>Delete User</h3>
              <p>Delete <strong>{deleteUser.name}</strong>? This action cannot be undone.</p>
              {deleteError && <p className="error-text">{deleteError}</p>}
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => { setDeleteUser(null); setDeleteError(""); }}>
                Cancel
              </button>
              <button type="button" className="danger-primary" onClick={confirmDeleteUser}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {resetUser && (
        <div className="manage-users-modal-overlay">
          <div className="manage-users-modal confirm-modal">
            <form onSubmit={submitResetPassword}>
              <div className="modal-header">
                <h3>Reset Password</h3>
                <p>Set a new password for <strong>{resetUser.name}</strong>.</p>
                {resetError && <p className="error-text">{resetError}</p>}
              </div>
              <div className="manage-users-edit-modal-content">
                <div className="modal-field">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    placeholder="Minimum 6 characters"
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => { setResetUser(null); setResetPassword(""); setResetError(""); }}>
                  Cancel
                </button>
                <button type="submit" className="primary">
                  Reset Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmInactiveUser && (
        <div className="manage-users-modal-overlay">
          <div className="manage-users-modal">
            <div className="modal-header">
              <h3>Confirm Inactive</h3>
              <p>
                Are you sure you want to mark{" "}
                <strong>{confirmInactiveUser.name}</strong> as inactive?
              </p>
              {confirmError && <p className="error-text">{confirmError}</p>}
            </div>
            <div className="modal-actions">
              <button type="button" onClick={cancelConfirmInactive}>
                Cancel
              </button>
              <button type="button" className="primary" onClick={confirmInactive}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {statusUser && (
        <div className="manage-users-modal-overlay">
          <div className="manage-users-modal">
            <form onSubmit={submitResignation}>
              <div className="modal-header">
                <h3>Resignation Date</h3>
                <p>Please provide a resignation date for inactive users.</p>
                {statusError && <p className="error-text">{statusError}</p>}
              </div>
              <div className="manage-users-edit-modal-content">
                <div className="modal-field">
                  <label>Resignation Date</label>
                  <input
                    type="date"
                    value={statusDate}
                    onChange={(e) => setStatusDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={closeStatusModal}>
                  Cancel
                </button>
                <button type="submit" className="primary">
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editUser && (
        <div className="manage-users-modal-overlay">
          <div className="manage-users-modal">
            <form onSubmit={submitEdit}>
              <div className="modal-header">
                <h3>Edit User</h3>
                {editError && <p className="error-text">{editError}</p>}
              </div>
              <div className="manage-users-edit-modal-content">
                <div className="modal-field">
                  <label>Employee ID</label>
                  <input type="text" value={editUser.employeeId || ""} disabled />
                </div>
                <div className="modal-field">
                  <label>Name</label>
                  <input
                    type="text"
                    value={editUser.name}
                    onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                  />
                </div>
                <div className="modal-field">
                  <label>Username</label>
                  <input
                    type="text"
                    value={editUser.username}
                    onChange={(e) => setEditUser({ ...editUser, username: e.target.value })}
                  />
                </div>
                <div className="modal-field">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editUser.email}
                    onChange={(e) => setEditUser({ ...editUser, email: e.target.value })}
                  />
                </div>
                <div className="modal-field">
                  <label>Role</label>
                  <select
                    value={editUser.role}
                    onChange={(e) => setEditUser({ ...editUser, role: e.target.value })}
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="modal-field">
                  <label>Assigned Role</label>
                  <select
                    value={editUser.roleId || ""}
                    onChange={(e) => setEditUser({ ...editUser, roleId: e.target.value })}
                  >
                    <option value="">Select Role</option>
                    {roleOptions.map((roleOption) => (
                      <option key={roleOption._id} value={roleOption._id}>
                        {roleOption.roleName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="modal-field">
                  <label>Reporting Manager</label>
                  <select
                    value={editUser.reportingManagerId || ""}
                    onChange={(e) => setEditUser({ ...editUser, reportingManagerId: e.target.value })}
                  >
                    <option value="">Select Reporting Manager</option>
                    {adminOptions.map((admin) => (
                      <option key={admin._id} value={admin._id}>
                        {admin.name} {admin.employeeId ? `- ${admin.employeeId}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="modal-field">
                  <label>Status</label>
                  <select
                    value={editUser.status}
                    onChange={(e) => {
                      const nextStatus = e.target.value;
                      setEditUser({
                        ...editUser,
                        status: nextStatus,
                        resignationDate: nextStatus === "inactive" ? editUser.resignationDate : ""
                      });
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                {editUser.status === "inactive" && (
                  <div className="modal-field">
                    <label>Resignation Date</label>
                    <input
                      type="date"
                      value={editUser.resignationDate || ""}
                      onChange={(e) => setEditUser({ ...editUser, resignationDate: e.target.value })}
                    />
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" onClick={closeEdit}>
                  Cancel
                </button>
                <button type="submit" className="primary">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageUsers;
